import { motion } from "framer-motion";
import {
  Code2,
  Palette,
  Megaphone,
  Bot,
  Sparkles,
  ArrowRight,
  Computer,
} from "lucide-react";

function Courses() {
  const courses = [
    {
      icon: <Code2 size={45} />,
      title: "Web Development",
      duration: "9 Months",
      color: "bg-blue-600",
      skills: [
        "HTML, CSS & JavaScript",
        "React.js",
        "Node.js & Express",
        "MongoDB",
        "REST APIs",
      ],
    },
    {
      icon: <Palette size={45} />,
      title: "Graphic Design",
      duration: "5 Months",
      color: "bg-pink-500",
      skills: [
        "Photoshop",
        "Illustrator",
        "Canva",
        "Branding",
        "Packaging Design",
      ],
    },
    {
      icon: <Megaphone size={45} />,
      title: "Digital Marketing",
      duration: "5 Months",
      color: "bg-green-500",
      skills: [
        "SEO",
        "Meta Ads",
        "Google Ads",
        "Analytics",
        "Content Marketing",
      ],
    },
    {
      icon: <Bot size={45} />,
      title: "Agent AI",
      duration: "6 Months",
      color: "bg-violet-500",
      skills: [
        "AI Agents",
        "Automation",
        "Prompt Engineering",
        "AI Workflows",
        "Business AI",
      ],
    },
    {
      icon: <Sparkles size={45} />,
      title: "Generative AI",
      duration: "6 Months",
      color: "bg-orange-500",
      skills: [
        "ChatGPT",
        "Gemini",
        "Claude",
        "AI Images",
        "AI Productivity",
      ],
    },
    {
      icon: <Computer size={45} />,
      title: "Other Courses",
      duration: "2-3 Months",
      color: "bg-yellow-500",
      skills: [
        "Basic Computer (2 Months)",
        "Tally (3 Months)",
      ],
    },
  ];

  return (
    <section
      id="courses"
      className="py-24 bg-gradient-to-br from-gray-50 via-white to-blue-50 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Heading */}

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="bg-blue-100 text-blue-700 px-5 py-2 rounded-full font-semibold">
            Our Courses
          </span>

          <h2 className="text-4xl lg:text-5xl font-bold mt-6">
            Build Skills for the
            <span className="text-blue-700"> Future</span>
          </h2>

          <p className="mt-5 text-gray-600 max-w-3xl mx-auto leading-8">
            Industry-oriented courses designed to help students,
            professionals, and entrepreneurs gain practical digital skills.
          </p>
        </motion.div>

        {/* Cards */}

        <motion.div
          className="grid md:grid-cols-2 xl:grid-cols-3 gap-8"
          variants={{
            hidden: {},
            show: {
              transition: {
                staggerChildren: 0.2,
              },
            },
          }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {courses.map((course, index) => (
            <motion.div
              key={index}
              variants={{
                hidden: {
                  opacity: 0,
                  y: 70,
                  scale: 0.9,
                },
                show: {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                },
              }}
              transition={{
                duration: 0.6,
              }}
              whileHover={{
                y: -12,
                scale: 1.03,
                rotate: -1,
              }}
              className="bg-white rounded-3xl shadow-lg hover:shadow-2xl overflow-hidden border border-gray-100"
            >
              {/* Top */}

              <div className={`${course.color} p-8 text-white relative`}>
                {/* Floating Icon */}

                <motion.div
                  animate={{
                    y: [0, -8, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  {course.icon}
                </motion.div>

                <h3 className="text-2xl font-bold mt-5">
                  {course.title}
                </h3>

                <p className="mt-2 opacity-90">
                  Duration : {course.duration}
                </p>
              </div>

              {/* Skills */}

              <div className="p-8">
                <h4 className="font-semibold text-gray-800 mb-4">
                  What You'll Learn
                </h4>

                <ul className="space-y-3">
                  {course.skills.map((skill, i) => (
                    <motion.li
                      key={i}
                      initial={{
                        opacity: 0,
                        x: -20,
                      }}
                      whileInView={{
                        opacity: 1,
                        x: 0,
                      }}
                      viewport={{ once: true }}
                      transition={{
                        delay: i * 0.08,
                        duration: 0.4,
                      }}
                      className="flex items-center gap-3 text-gray-600"
                    >
                      <motion.span
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        transition={{
                          delay: i * 0.1,
                          type: "spring",
                          stiffness: 300,
                        }}
                        className="text-blue-600"
                      >
                        ✔
                      </motion.span>

                      {skill}
                    </motion.li>
                  ))}
                </ul>

                {/* Button */}

                <motion.button
                  whileHover={{
                    scale: 1.04,
                  }}
                  whileTap={{
                    scale: 0.95,
                  }}
                  className="mt-8 w-full bg-blue-700 hover:bg-blue-800 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
                >
                  Learn More

                  <motion.div
                    whileHover={{
                      x: 5,
                    }}
                    transition={{
                      duration: 0.2,
                    }}
                  >
                    <ArrowRight size={18} />
                  </motion.div>
                </motion.button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default Courses;